import React, { useEffect, useState } from 'react';

import { useReschedule } from '../../hooks/schedule/useReschedule';
import { ScheduleAppointmentSectionView } from '../../components/ScheduleAppointment/AppointmentDisplay/ScheduleAppointmentSectionView';
import { InfoMessage } from '../../components/InfoMessage';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { api } from 'common';
import { ModifyAppointmentModal } from './ModifyAppointmentModal';

import { Loader } from '@vivaahealth/design-system';

interface Props {
  rescheduleIdentifier: api.EhrTimeSlot.RescheduleIdentifier;
  timezone: string;
  setIsRescheduling: (val: boolean) => void;
  onRescheduled?: () => void;
  setSlot?: (slot: api.EhrTimeSlot.EhrTimeSlot) => void;
  onRequiresManual?: () => void;
}

export const RescheduleView: React.FC<Props> = ({
  rescheduleIdentifier,
  timezone,
  onRescheduled,
  setIsRescheduling,
  setSlot,
  onRequiresManual,
}) => {
  const { t } = useTranslation();
  const { authenticationCode } = useParams<{ authenticationCode?: string }>();
  const {
    availableEhrTimeSlots,
    onSlotSelected,
    pending,
    showManualRescheduling,
    error,
    setRescheduleSlot,
    rescheduleSlot,
  } = useReschedule({
    rescheduleIdentifier,
    onRescheduled,
    authenticationCode,
  });
  const [rescheduleConfirmationModalOpen, setRescheduleConfirmationModalOpen] =
    useState(false);

  const rescheduleConfirmModal = (
    <ModifyAppointmentModal
      title={t('Reschedule Appointment')}
      instructions={t(
        'Are you sure you want to reschedule? This action cannot be undone. Rescheduled appointments will need to be re-confirmed closer to the appointment date.',
      )}
      isOpen={rescheduleConfirmationModalOpen}
      setModalOpen={setRescheduleConfirmationModalOpen}
      onModifyAppointment={() => {
        if (rescheduleSlot) {
          setIsRescheduling(true);
          onSlotSelected(rescheduleSlot, timezone);
          if (setSlot) {
            setSlot(rescheduleSlot);
          }
        }
      }}
      pending={pending}
    />
  );

  useEffect(() => {
    if (!onRequiresManual) return;
    if (!showManualRescheduling) return;
    onRequiresManual();
  }, [showManualRescheduling, onRequiresManual]);

  if (error) {
    setIsRescheduling(false);
    return (
      <InfoMessage isError={true}>
        {t('There was an error processing your rescheduling request.')}
      </InfoMessage>
    );
  }
  if (!availableEhrTimeSlots) {
    return <Loader label={t('Fetching available time slots...')} />;
  }
  if (pending) {
    return <Loader label={t('Rescheduling appointment...')} />;
  }
  return (
    <>
      {rescheduleConfirmModal}
      <ScheduleAppointmentSectionView
        key={
          'ehrTimeSlotBookingId' in rescheduleIdentifier
            ? rescheduleIdentifier.ehrTimeSlotBookingId
            : rescheduleIdentifier.existingEncounterId
        }
        availableEhrTimeSlots={availableEhrTimeSlots}
        onSlotSelected={(slot) => {
          setRescheduleConfirmationModalOpen(true);
          setRescheduleSlot(slot);
        }}
        pending={pending}
        timezone={timezone}
      />
    </>
  );
};
